import { HomeyAPI, type UpdateInfo, type UpdateState } from 'homey-api';
import { shallow } from 'zustand/shallow';

import { ToastManager } from '../../../ToastManager.jsx';

import { BaseApiStore, type FetchArgs } from '../../../store/BaseApiStore.ts';
import { selectedHomeyStore } from '../../../store/SelectedHomeyStore';
import { selectBase } from '../../../store/selectors.ts';
import { useAttach } from '../../../store/useAttach';

interface State {
  updateData: UpdateInfo | null;
  updateStateData: UpdateState | null;
  optionAutoupdateData: boolean | null;
  isLoading: boolean;
  isRefetchingUpdates: boolean;
  error: unknown;
}

type UpdatesSettingsStoreState = State;

export class UpdatesSettingsStore extends BaseApiStore<State> {
  constructor() {
    super('updates-settings');
  }

  override createInitialState() {
    return {
      updateData: null,
      updateStateData: null,
      optionAutoupdateData: false,
      isLoading: true,
      isRefetchingUpdates: false,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    if (HomeyAPI.isHomeyAPIV3Cloud(api) === true) {
      this.set({ ...this.createInitialState(), isLoading: false });
      return;
    }

    try {
      const updatePromise = UpdatesSettingsStore.getUpdates(api);

      let optionAutoupdatePromise;

      if (selectedHomeyStore.get().selectedHomey?.model === 'shs') {
        optionAutoupdatePromise = Promise.resolve({ value: null });
      } else {
        optionAutoupdatePromise = api.updates.getOptionAutoupdate();
      }
      const updateStatePromise = api.updates.getState();

      const [updateData, optionAutoupdateData, updateStateData] = await Promise.all([
        updatePromise,
        optionAutoupdatePromise,
        updateStatePromise,
      ]);

      if (abortController.signal.aborted) {
        return;
      }

      this.set({
        ...this.createInitialState(),
        updateData: updateData[updateData.length - 1] ?? null,
        updateStateData: updateStateData,
        optionAutoupdateData: optionAutoupdateData.value,
        isLoading: false,
      });
      await this.connectManager({ manager: api.updates });
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }

      if (fetchPolicy !== 'background') {
        ToastManager.handleError(error);
      }

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy(reason: string) {
    this.disconnectManager().catch(console.error);
  }

  use() {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAttach(this, this.key);

    return { updatesSettings: this.store(selectAll, shallow) };
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    if (HomeyAPI.isHomeyAPIV3Cloud(api) === true) {
      return;
    }

    api.updates.on('state', this.onState);
    api.updates.on('download_start', this.onDownloadStart);
    api.updates.on('download_progress', this.onDownloadProgress);
    api.updates.on('download_done', this.onDownloadDone);
    api.updates.on('download_error', this.onDownloadError);
    api.updates.on('install_start', this.onInstallStart);
    api.updates.on('install_done', this.onInstallDone);

    if (HomeyAPI.isHomeyAPIV2(api) === true) {
      api.updates.on('install_error', this.onInstallError);
    }
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    if (HomeyAPI.isHomeyAPIV3Cloud(api) === true) {
      return;
    }

    api.updates.off('state', this.onState);
    api.updates.off('download_start', this.onDownloadStart);
    api.updates.off('download_progress', this.onDownloadProgress);
    api.updates.off('download_done', this.onDownloadDone);
    api.updates.off('download_error', this.onDownloadError);
    api.updates.off('install_start', this.onInstallStart);
    api.updates.off('install_done', this.onInstallDone);

    if (HomeyAPI.isHomeyAPIV2(api) === true) {
      api.updates.off('install_error', this.onInstallError);
    }
  }

  private onState = (state: UpdateState) => {
    console.log('onState');
    console.log(state);

    this.set((prevState) => {
      if (prevState.updateStateData == null) {
        return {};
      }

      return {
        updateStateData: { ...prevState.updateStateData, ...state },
      };
    });
  };

  private onDownloadStart = () => {
    console.log('onDownloadStart');
  };

  private onDownloadProgress = ({ percent }: { percent: number }) => {
    console.log('onDownloadProgress');
    this.set((prevState) => {
      if (prevState.updateStateData == null) return {};

      return {
        updateStateData: { ...prevState.updateStateData, downloads_progress: percent / 100 },
      };
    });
  };

  private onDownloadDone = () => {
    console.log('onDownloadDone');
    this.emitter.emit('download_done');
  };

  private onDownloadError = ({ message }: { message: string }) => {
    console.log('onDownloadError', message);
    this.set((prevState) => {
      if (prevState.updateStateData == null) return {};

      return {
        updateStateData: { ...prevState.updateStateData, error: message },
      };
    });
  };

  private onInstallStart = () => {
    console.log('onInstallStart');
  };

  private onInstallDone = ({ bootId }: { bootId: string }) => {
    console.log('onInstallDone', { bootId });
    this.emitter.emit('install_done', { bootId });
  };

  // Only on platform version 1.
  private onInstallError = ({ message }: { message: string }) => {
    console.log('onInstallError');
    this.set((prevState) => {
      if (prevState.updateStateData == null) return {};

      return {
        updateStateData: { ...prevState.updateStateData, error: message },
      };
    });
  };

  async installUpdate() {
    const api = this.getApi();

    if (HomeyAPI.isHomeyAPIV3Cloud(api) === true) {
      throw new Error('Software updates are not supported on Homey Cloud');
    }

    await api.updates.installUpdate({});
  }

  async refetchUpdates() {
    const api = this.getApi();

    if (HomeyAPI.isHomeyAPIV3Cloud(api) === true) {
      throw new Error('Software updates are not supported on Homey Cloud');
    }

    const state = this.get();

    if (state.isRefetchingUpdates === true) {
      return;
    }

    this.set({
      isRefetchingUpdates: true,
    });

    let updateData;

    try {
      updateData = await UpdatesSettingsStore.getUpdates(api);
    } catch (error) {
      this.set({
        isRefetchingUpdates: false,
      });
      throw error;
    }

    this.set({
      updateData: updateData[0] ?? null,
      isRefetchingUpdates: false,
    });

    return updateData;
  }

  static async getUpdates(api: HomeyAPI): Promise<UpdateInfo[]> {
    if (HomeyAPI.isHomeyAPIV2(api) === true) {
      return await api.updates.getUpdates({ cache: 'false' });
    }

    if (HomeyAPI.isHomeyAPIV3Local(api) === true) {
      return await api.updates.getUpdates();
    }

    throw new Error('Software updates are not supported on Homey Cloud');
  }
}

function selectAll(state: UpdatesSettingsStoreState) {
  return {
    ...selectBase(state),
    updateData: state.updateData,
    optionAutoupdateData: state.optionAutoupdateData,
    updateStateData: state.updateStateData,
    isRefetchingUpdates: state.isRefetchingUpdates,
  };
}

export const updatesSettingsStore = new UpdatesSettingsStore();
